import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Award,
  BarChart3,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";
import { CorporateReportsList } from "@/components/players/CorporateReportsList";
import { CtfReportsList } from "@/components/players/CtfReportsList";
import { SquadReportsList } from "@/components/players/SquadReportsList";
import type { TReportItem, TReportList } from "@/types/reports";
import { PlayerService } from "@/lib/services/player";

export default function PlayerReportPage() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<{
    id: string;
    user_full_name: string;
  } | null>(null);
  const [reportsData, setReportsData] = useState<TReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setIsLoading(true);
    setError(null);

    PlayerService.getPlayerReports(id)
      .then((r: TReportList) => {
        if (!mounted) return;
        setUser({
          id: r.id,
          user_full_name: r.user_full_name,
        });
        setReportsData(r.report_data ?? []);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load reports");
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  const totalReports = reportsData?.length ?? 0;
  const totalScore =
    reportsData?.reduce(
      (sum, report) => sum + Number(report.total_obtained_score ?? 0),
      0
    ) ?? 0;
  const totalPossibleScore =
    reportsData?.reduce(
      (sum, report) => sum + Number(report.total_score ?? 0),
      0
    ) ?? 0;
  const averageScore =
    totalPossibleScore > 0
      ? Math.round((totalScore / totalPossibleScore) * 100)
      : 0;

  if (isLoading) {
    return (
      <div className="space-y-6 pb-8">
        <div className="rounded-2xl p-10 bg-muted animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="h-14 bg-muted rounded animate-pulse" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="h-14 bg-muted rounded animate-pulse" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="h-14 bg-muted rounded animate-pulse" />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="h-32 bg-muted rounded animate-pulse" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/30">
        <CardContent className="p-6">
          <p className="text-sm text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-accent p-8 md:p-10 shadow-2xl">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white blur-3xl" />
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white blur-3xl" />
        </div>

        <div className="relative z-10">
          <Link to={id ? `/players/${id}` : "/players"}>
            <Button
              variant="secondary"
              size="sm"
              className="mb-6 gap-2 bg-white/20 hover:bg-white/30 text-primary-foreground border-white/20 backdrop-blur-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Player
            </Button>
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Trophy className="h-7 w-7 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-primary-foreground">
                    Performance Reports
                  </h1>
                  <p className="text-primary-foreground/80 text-sm md:text-base mt-1">
                    {(user?.user_full_name ?? "Player") + "'s Completed Scenarios"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 min-w-[100px]">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="h-4 w-4 text-primary-foreground/80" />
                  <p className="text-xs text-primary-foreground/80 font-medium">
                    Total
                  </p>
                </div>
                <p className="text-2xl font-bold text-primary-foreground">
                  {totalReports}
                </p>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 min-w-[100px]">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-primary-foreground/80" />
                  <p className="text-xs text-primary-foreground/80 font-medium">
                    Avg Score
                  </p>
                </div>
                <p className="text-2xl font-bold text-primary-foreground">
                  {averageScore}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium mb-1">
                  Total Score
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {totalScore}
                  <span className="text-lg text-muted-foreground font-normal">
                    /{totalPossibleScore}
                  </span>
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium mb-1">
                  Scenarios
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {totalReports}
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium mb-1">
                  Success Rate
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {averageScore}%
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border bg-card shadow-lg">
        <CardContent className="p-6">
          <Tabs defaultValue="corporate" className="w-full">
            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <h2 className="text-xl font-bold text-foreground">
                Scenario Reports
              </h2>
              <TabsList className="bg-muted p-1 h-11">
                <TabsTrigger
                  value="corporate"
                  className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow"
                >
                  <Target className="h-4 w-4" />
                  <span>Corporate</span>
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {totalReports}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="ctf"
                  className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow"
                >
                  <Trophy className="h-4 w-4" />
                  <span>CTF</span>
                </TabsTrigger>
                <TabsTrigger
                  value="squad"
                  className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow"
                >
                  <Users className="h-4 w-4" />
                  <span>Squad</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <div>
              <TabsContent value="corporate" className="mt-0">
                <CorporateReportsList reportsData={reportsData} />
              </TabsContent>
              <TabsContent value="ctf" className="mt-0">
                <CtfReportsList />
              </TabsContent>
              <TabsContent value="squad" className="mt-0">
                <SquadReportsList />
              </TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
